/*
 * Use these helpers to create action-creators.
 *
 * `createAction` will create a constant and an action creater
 * based on the provided `name`.
 * `createAction('SOMETHING_HAPPENED')` will return
 * ```
 * {
 *    'SOMETHING_HAPPENED': 'SOMETHING_HAPPENED',
 *    somethingHappened: (data) => ({ type: 'SOMETHING_HAPPENED', data: data })
 * }
 * ```
 *
 * `createNetworkActions` will create three pairs of constants and functions
 * with the suffixes `_REQUEST`, `_RESPONSE` and `_FAILURE`.
 */

import { camel, constant } from "../caseUtils";

type SnakeCaseToCamelCase<S extends string> = S extends `${infer T}_${infer U}` ? `${Lowercase<T>}${Capitalize<SnakeCaseToCamelCase<U>>}` : Lowercase<S>;

type ActionFunction<T extends string> = <U>(data?: U) => {
	data: U;
	type: Uppercase<T>;
};

type CreateAction<T extends string> = Record<Uppercase<T>, Uppercase<T>> & Record<SnakeCaseToCamelCase<T>, ActionFunction<T>>;

export type Action<T = null> = T extends null
	? {
			data?: any;
			type: string;
		}
	: {
			data: T;
			type: string;
		};

export const createAction = <T extends string>(name: T): CreateAction<T> => {
	const ACTION_NAME = constant(name);

	return {
		[ACTION_NAME]: ACTION_NAME,
		[camel(name)]: (data?: any) => ({
			data,
			type: ACTION_NAME,
		}),
	} as CreateAction<T>;
};

export const createNetworkActions = <T extends string>(name: T) => ({
	...createAction(`${name}_REQUEST`),
	...createAction(`${name}_RESPONSE`),
	...createAction(`${name}_FAILURE`),
});
